import { useMemo } from "react";
import { Chart } from "react-chartjs-2";
import * as ChartGeo from "chartjs-chart-geo";
import {
  Chart as ChartJS,
  CategoryScale,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import { Box } from "@mui/material";
import WidgetCard from "@arelia-components/ui-atoms/WidgetCard";
import { ByCountry, FollowersByCountry } from "@arelia-types/index";
import { geodata } from "./geodata";
import getChartOptions from "./options";
import { calcBackgroundColor, calcPercentage } from "./utils";

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  CategoryScale,
  ChartGeo.ChoroplethController,
  ChartGeo.ProjectionScale,
  ChartGeo.ColorScale,
  ChartGeo.GeoFeature
);

type ChartData = {
  feature: any;
  value: number;
  label: string;
  countryKey: string
};

type Props = {
  countries: FollowersByCountry[];
  loading: boolean;
  error: boolean;
  id: string;
  unconnected: boolean;
  headerComponent?: JSX.Element;
};

export default function CountriesPresenter({ countries, loading, error, id, unconnected, headerComponent }: Props) {
  const averageByCountry = useMemo(() => {
    const countryNames = geodata.objects.countries1.geometries.filter(x => x.properties["Alpha-2"])
      .map(c => ({ name: c.properties.name, code: c.properties["Alpha-2"]})) as { code: string, name: string }[];

    // creating the object with country names as keys and average percentages as values
    return countryNames.reduce((obj, countryName) => {
      const totalPercentage = countries.reduce((sum, { byCountry, totalByCountry }) => {
        const percentage = calcPercentage(byCountry[countryName.code] ?? byCountry[countryName.name] ?? 0, totalByCountry);
        return sum + percentage;
      }, 0);

      obj[countryName.code] = +(totalPercentage / countries.filter(({ byCountry }) => byCountry.hasOwnProperty(countryName.code)
        || byCountry.hasOwnProperty(countryName.name)).length).toFixed(2) || 0;
      return obj;
    }, {} as ByCountry);
  }, [countries]);

  const data = useMemo(() =>
    ChartGeo.topojson.feature(
      //@ts-ignore
      geodata,
      geodata.objects.countries1
      //@ts-ignore
    ).features.map((d: any) => {
      return ({
        ...d, properties: {
          ...d.properties,
          backgroundColor: calcBackgroundColor(averageByCountry, d.properties["Alpha-2"])
        }
      })
    }), [averageByCountry]);

  const chartData = useMemo(() => {
    return {
      labels: data.map((d: any) => d.properties.name),
      datasets: [
        {
          outline: data,
          label: "Countries",
          backgroundColor: data.map((d: any) => d.properties.backgroundColor),
          data: data.map((d: any) => {
            return {
              feature: d,
              value: averageByCountry?.[d.properties["Alpha-2"]] || 0,
              label: d.properties.name,
              countryKey: d.properties["Alpha-2"],
            };
          }),
        },
      ],
    }
  }, [data, averageByCountry]);

  const tooltipTitleFunction = (tooltipItems: any[]) => {
    const raw = tooltipItems[0]?.raw as ChartData;
    return `${raw.label} - ${tooltipItems[0].formattedValue}%`;
  }

  const tooltipLabelFunction = (tooltipItem: any) => {
    const raw = tooltipItem.raw as ChartData;
    return countries.map(({ networkName, byCountry, totalByCountry }) => {
      const value = byCountry[raw.countryKey] ?? byCountry[raw.label] ?? 0;
      const percentage = +calcPercentage(value, totalByCountry).toFixed(2);
      return `${networkName}: ${value} (${percentage || 0}%)`;
    });
  }

  const options = getChartOptions(tooltipTitleFunction, tooltipLabelFunction);

  return (
    <WidgetCard
      widgetId={id}
      title="Top Countries"
      loading={loading}
      error={error}
      unconnected={unconnected}
      headerComponent={headerComponent}
    >
      <Box width={1} >
        <Chart
          type="choropleth"
          data={chartData}
          options={options}
        />
      </Box>
    </WidgetCard>
  );
}
